/**
 * Shared chrome for the two public legal pages (Privacy, Terms).
 *
 * Intentionally minimal — a top bar with the Lenavio wordmark + back link,
 * a centred prose column for the document body, and a footer that
 * cross-links to the other legal page. No AuthProvider / AppShell wrapping
 * so these pages are reachable without a session.
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";

interface LegalLayoutProps {
  title: string;
  effectiveDate: string;
  /** Where the "Back" link should point. Defaults to /login. */
  backTo?: string;
  backLabel?: string;
  /** Which sibling page to cross-link at the bottom. */
  sibling: { to: string; label: string };
  children: ReactNode;
}

export function LegalLayout({
  title,
  effectiveDate,
  backTo = "/login",
  backLabel = "Back",
  sibling,
  children,
}: LegalLayoutProps) {
  return (
    <div className="min-h-screen bg-background text-text-primary">
      {/* Top bar */}
      <header className="border-b border-border bg-surface">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="Lenavio home">
            <img src="/logo.png" alt="Lenavio" className="h-7 w-auto" />
          </Link>
          <Link
            to={backTo}
            className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {backLabel}
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-10">
          <h1 className="text-4xl font-bold font-heading tracking-tight">
            {title}
          </h1>
          <p className="text-sm text-text-muted mt-2">
            Effective date: {effectiveDate}
          </p>
        </div>

        <article className="legal-prose space-y-6 text-[15px] leading-relaxed text-text-primary">
          {children}
        </article>

        {/* Footer cross-link */}
        <div className="mt-16 pt-8 border-t border-border flex items-center justify-between text-sm text-text-muted">
          <Link
            to={sibling.to}
            className="text-accent hover:underline"
          >
            {sibling.label}
          </Link>
          <span>© {new Date().getFullYear()} Lenavio, Inc.</span>
        </div>
      </main>
    </div>
  );
}
